/*
 * COMPONENTE ÚNICO DE AVISO DE ERROR DEL NÚCLEO (RF-139, D-216).
 *
 * Toda pantalla de todo módulo pinta sus errores con esto. Antes cada pantalla
 * dibujaba su propia franja roja leyendo `error.mensaje` a mano, y como el error
 * del cliente de API guardaba el texto en `message` (nombre del Error nativo),
 * la mayoría pintaba el recuadro VACÍO: ése es el hallazgo H-14 y su causa raíz
 * está descrita en RN-349.
 *
 * ORDEN FIJO, el del prototipo v24 (diseno/prototipo-v24-aviso-error.html):
 *   1. mensaje en lenguaje llano, primero y siempre (RN-348);
 *   2. detalles por campo, uno por renglón, si vienen (RN-351);
 *   3. código del catálogo de la API al final, atenuado y chico (RN-333, D-211).
 *
 * REGLAS QUE HACE CUMPLIR, para que nadie las reimplemente (RNF-068):
 *   * Sin texto NO se dibuja nada: una franja roja sin una palabra es un defecto,
 *     no un estado del sistema (RN-348c).
 *   * El texto del servidor se muestra TAL CUAL: no se traduce, no se acorta y no
 *     se sustituye (RN-353, D-219).
 *   * Nunca se pinta el nombre de una excepción ni una traza; si eso es lo único
 *     que hay, se usa el mensaje de respaldo (RN-350 → RN-348b, resuelto en el
 *     cliente de API).
 *   * El código NUNCA va primero, nunca va solo y nunca sustituye al mensaje
 *     (RN-333).
 *   * Se anuncia a lectores de pantalla al aparecer y se lee completo a 320 px
 *     (RNF-069): `role="alert"` y quiebre de palabra larga.
 *
 * Reversibilidad de D-211: para que el código deje de verse en TODA la
 * aplicación basta poner MOSTRAR_CODIGO_DE_ERROR en false. Un solo lugar.
 */

import { mensajeDeError } from '../api/cliente.js';

/** D-211: interruptor único del código visible. En false desaparece de toda la app. */
export const MOSTRAR_CODIGO_DE_ERROR = true;

/**
 * Códigos que NO se muestran porque el aviso no es su forma de presentarse
 * (RN-352): la falta de conexión y la sesión terminada ya tienen tratamiento
 * decidido y el código no le dice nada a la persona en esos dos casos.
 */
const CODIGOS_SIN_MOSTRAR = new Set(['SIN_CONEXION', 'SESION_REQUERIDA']);

function nombreLlanoDelCampo(campo) {
  if (!campo) return null;
  // El servidor manda el nombre del campo tal como lo nombra el contrato
  // ("importe", "fechaDeMovimiento"). Se presenta en palabras (RN-351).
  const conEspacios = String(campo)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .toLowerCase()
    .trim();
  if (!conEspacios) return null;
  return conEspacios.charAt(0).toUpperCase() + conEspacios.slice(1);
}

/**
 * @param error      Error del cliente de API (ErrorDeApi) o cualquier Error.
 * @param mensaje    Texto propio del frontend, para validaciones que hace el
 *                   teléfono antes de mandar nada (RN-353). En ese caso no hay
 *                   código que mostrar.
 * @param detalles   Lista [{ campo, mensaje }]. Si no se pasa, se toman del error.
 * @param alCerrar   Si se pasa, se dibuja el botón de cerrar (RN-354).
 * @param acciones   Nodo opcional debajo del aviso (por ejemplo, Reintentar).
 */
export default function AvisoDeError({
  error = null,
  mensaje = null,
  detalles = null,
  alCerrar = null,
  acciones = null,
  margen = undefined,
  datoDePrueba = 'aviso-de-error',
}) {
  const texto = mensaje ?? mensajeDeError(error);

  // RN-348c: sin una palabra que decir, no se dibuja el aviso.
  if (!texto) return null;

  const lista = detalles ?? error?.detalles ?? [];
  const codigo = error?.codigo ?? null;
  const codigoVisible =
    MOSTRAR_CODIGO_DE_ERROR && !mensaje && codigo && !CODIGOS_SIN_MOSTRAR.has(codigo) ? codigo : null;

  return (
    <div
      className="aviso e aviso-de-error"
      role="alert"
      data-prueba={datoDePrueba}
      style={margen !== undefined ? { margin: margen } : undefined}
    >
      <span className="aviso-de-error__signo" aria-hidden="true">✖</span>
      <span className="aviso-de-error__cuerpo">
        <span className="aviso-de-error__mensaje" data-prueba="aviso-de-error-mensaje">{texto}</span>

        {lista.length > 0 && (
          <ul className="aviso-de-error__detalles" data-prueba="aviso-de-error-detalles">
            {lista.map((detalle, indice) => {
              const nombre = nombreLlanoDelCampo(detalle?.campo);
              return (
                <li key={detalle?.campo ?? indice}>
                  {nombre && <b>{nombre}: </b>}
                  {detalle?.mensaje}
                </li>
              );
            })}
          </ul>
        )}

        {codigoVisible && (
          <span className="aviso-de-error__codigo" data-prueba="aviso-de-error-codigo">{codigoVisible}</span>
        )}

        {acciones && <span className="aviso-de-error__acciones">{acciones}</span>}
      </span>

      {alCerrar && (
        <button
          type="button"
          className="aviso-de-error__cerrar"
          onClick={alCerrar}
          aria-label="Cerrar el aviso"
        >
          ✕
        </button>
      )}
    </div>
  );
}
